<script setup>
// Отчёт по звонкам. Отчёты Chatwoot считают диалоги и сообщения, а смене нужна
// своя картина: сколько входящих приняли, сколько потеряли и сколько исходящих
// сделал каждый администратор — у клиентов на это есть дневная норма.
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import CallReportsAPI from 'dashboard/api/callReports';
import { GROUP_BY_FILTER } from './constants';
import ReportHeader from './components/ReportHeader.vue';
import ReportFilters from './components/ReportFilters.vue';
import ReportMetricCard from './components/ReportMetricCard.vue';
import BarChart from 'shared/components/charts/BarChart.vue';
import V4Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();

const report = ref(null);
const isLoading = ref(false);
const range = ref({ since: null, until: null, groupBy: 'day' });

const summary = computed(() => report.value?.summary || {});
const agents = computed(() => report.value?.agents || []);

// «45 звонков · 1 ч 20 мин» читается, «4817» — нет.
const formatDuration = seconds => {
  const total = Number(seconds) || 0;
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (hours) return t('CALL_REPORTS.DURATION.HM', { hours, minutes });
  if (minutes) return t('CALL_REPORTS.DURATION.MS', { minutes, seconds: secs });
  return t('CALL_REPORTS.DURATION.S', { seconds: secs });
};

const metrics = computed(() => [
  {
    key: 'incoming',
    label: t('CALL_REPORTS.METRICS.INCOMING'),
    info: t('CALL_REPORTS.METRICS.INCOMING_INFO'),
    value: String(summary.value.incoming ?? 0),
  },
  {
    key: 'incoming_answered',
    label: t('CALL_REPORTS.METRICS.ANSWERED'),
    info: t('CALL_REPORTS.METRICS.ANSWERED_INFO'),
    value: String(summary.value.incoming_answered ?? 0),
  },
  {
    key: 'incoming_missed',
    label: t('CALL_REPORTS.METRICS.MISSED'),
    info: t('CALL_REPORTS.METRICS.MISSED_INFO'),
    value: String(summary.value.incoming_missed ?? 0),
  },
  {
    key: 'outgoing',
    label: t('CALL_REPORTS.METRICS.OUTGOING'),
    info: t('CALL_REPORTS.METRICS.OUTGOING_INFO'),
    value: `${summary.value.outgoing ?? 0} / ${summary.value.outgoing_answered ?? 0}`,
  },
  {
    key: 'answer_speed',
    label: t('CALL_REPORTS.METRICS.ANSWER_SPEED'),
    info: t('CALL_REPORTS.METRICS.ANSWER_SPEED_INFO'),
    value: formatDuration(summary.value.avg_answer_seconds),
  },
  {
    key: 'fast_answer',
    label: t('CALL_REPORTS.METRICS.FAST_ANSWER', {
      seconds: summary.value.fast_answer_seconds ?? 20,
    }),
    info: t('CALL_REPORTS.METRICS.FAST_ANSWER_INFO', {
      seconds: summary.value.fast_answer_seconds ?? 20,
    }),
    value: `${summary.value.fast_answer_rate ?? 0}%`,
  },
  {
    key: 'abandon',
    label: t('CALL_REPORTS.METRICS.ABANDON_WAIT'),
    info: t('CALL_REPORTS.METRICS.ABANDON_WAIT_INFO'),
    value: formatDuration(summary.value.avg_abandon_seconds),
  },
  {
    key: 'talk',
    label: t('CALL_REPORTS.METRICS.TALK_TIME'),
    info: t('CALL_REPORTS.METRICS.TALK_TIME_INFO'),
    value: formatDuration(summary.value.talk_seconds),
  },
  {
    key: 'avg_talk',
    label: t('CALL_REPORTS.METRICS.AVG_TALK'),
    info: t('CALL_REPORTS.METRICS.AVG_TALK_INFO'),
    value: formatDuration(summary.value.avg_talk_seconds),
  },
]);

const pointLabel = timestamp => {
  const date = new Date(timestamp * 1000);
  return range.value.groupBy === 'hour'
    ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString([], { day: '2-digit', month: '2-digit' });
};

// Принятые и пропущенные стоят одним столбцом: важна не сумма звонков, а доля
// потерянных в этот день.
const chartCollection = computed(() => {
  const series = report.value?.series || [];
  return {
    labels: series.map(point => pointLabel(point.timestamp)),
    datasets: [
      {
        label: t('CALL_REPORTS.CHART.ANSWERED'),
        backgroundColor: '#1F93FF',
        stack: 'incoming',
        data: series.map(point => point.incoming_answered),
      },
      {
        label: t('CALL_REPORTS.CHART.MISSED'),
        backgroundColor: '#F2542D',
        stack: 'incoming',
        data: series.map(point => point.incoming_missed),
      },
      {
        label: t('CALL_REPORTS.CHART.OUTGOING'),
        backgroundColor: '#8D9CB4',
        stack: 'outgoing',
        data: series.map(point => point.outgoing),
      },
    ],
  };
});

// Часы суток: сутки в ряду по дням схлопнуты в точку, а смену ставят по часам —
// именно здесь видно, в какие часы звонки теряются. Пустые часы по краям режем,
// иначе половина графика — это ночь, когда клуб закрыт.
const hourlyCollection = computed(() => {
  const hours = report.value?.hourly || [];
  const isBusy = point =>
    point.incoming_answered + point.incoming_missed + point.outgoing > 0;
  const first = hours.findIndex(isBusy);
  const last = hours.findLastIndex(isBusy);
  const visible = first === -1 ? hours : hours.slice(first, last + 1);

  return {
    labels: visible.map(point => `${String(point.hour).padStart(2, '0')}:00`),
    datasets: [
      {
        label: t('CALL_REPORTS.CHART.ANSWERED'),
        backgroundColor: '#1F93FF',
        stack: 'incoming',
        data: visible.map(point => point.incoming_answered),
      },
      {
        label: t('CALL_REPORTS.CHART.MISSED'),
        backgroundColor: '#F2542D',
        stack: 'incoming',
        data: visible.map(point => point.incoming_missed),
      },
      {
        label: t('CALL_REPORTS.CHART.OUTGOING'),
        backgroundColor: '#8D9CB4',
        stack: 'outgoing',
        data: visible.map(point => point.outgoing),
      },
    ],
  };
});

const chartOptions = {
  scales: { x: { stacked: true }, y: { stacked: true, beginAtZero: true } },
  plugins: { legend: { display: true, position: 'bottom' } },
};

const fetchReport = async () => {
  if (!range.value.since) return;
  isLoading.value = true;
  try {
    const { data } = await CallReportsAPI.get(range.value);
    report.value = data;
  } catch (error) {
    useAlert(t('CALL_REPORTS.FETCH_FAILED'));
  } finally {
    isLoading.value = false;
  }
};

const onFilterChange = ({ from, to, groupBy }) => {
  range.value = {
    since: from,
    until: to,
    groupBy: groupBy?.period || GROUP_BY_FILTER[1].period,
  };
  fetchReport();
};

const downloadReport = async () => {
  try {
    const { data } = await CallReportsAPI.download(range.value);
    const blob = new Blob(['﻿', data], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `calls_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  } catch (error) {
    useAlert(t('CALL_REPORTS.FETCH_FAILED'));
  }
};
</script>

<template>
  <ReportHeader
    :header-title="$t('CALL_REPORTS.HEADER')"
    :header-description="$t('CALL_REPORTS.DESCRIPTION')"
  >
    <V4Button
      :label="$t('CALL_REPORTS.DOWNLOAD')"
      icon="i-ph-download-simple"
      size="sm"
      @click="downloadReport"
    />
  </ReportHeader>

  <div class="flex flex-col">
    <ReportFilters
      :show-entity-filter="false"
      :show-business-hours="false"
      show-group-by
      @filter-change="onFilterChange"
    />

    <div
      class="grid grid-cols-2 gap-4 px-6 py-5 mt-4 shadow outline outline-1 outline-n-container rounded-xl bg-n-solid-2 md:grid-cols-3 xl:grid-cols-5"
    >
      <ReportMetricCard
        v-for="metric in metrics"
        :key="metric.key"
        :label="metric.label"
        :value="metric.value"
        :info-text="metric.info"
      />
    </div>

    <div
      class="px-6 py-5 mt-4 shadow outline outline-1 outline-n-container rounded-xl bg-n-solid-2"
    >
      <h3 class="mb-3 text-sm font-medium text-n-slate-11">
        {{ $t('CALL_REPORTS.CHART.BY_DAY') }}
      </h3>
      <div v-if="isLoading" class="flex items-center justify-center h-64">
        <Spinner />
      </div>
      <div v-else class="h-64">
        <BarChart :collection="chartCollection" :chart-options="chartOptions" />
      </div>
    </div>

    <div
      class="px-6 py-5 mt-4 shadow outline outline-1 outline-n-container rounded-xl bg-n-solid-2"
    >
      <h3 class="mb-1 text-sm font-medium text-n-slate-11">
        {{ $t('CALL_REPORTS.CHART.BY_HOUR') }}
      </h3>
      <p class="mb-3 text-xs text-n-slate-11">
        {{ $t('CALL_REPORTS.CHART.BY_HOUR_HINT') }}
      </p>
      <div class="h-64">
        <BarChart
          :collection="hourlyCollection"
          :chart-options="chartOptions"
        />
      </div>
    </div>

    <div
      class="mt-4 shadow outline outline-1 outline-n-container rounded-xl bg-n-solid-2 overflow-x-auto"
    >
      <table class="w-full text-sm">
        <thead>
          <tr class="text-n-slate-11">
            <th class="px-6 py-3 font-medium text-left">
              {{ $t('CALL_REPORTS.TABLE.AGENT') }}
            </th>
            <th class="px-4 py-3 font-medium text-right">
              {{ $t('CALL_REPORTS.TABLE.INCOMING_ANSWERED') }}
            </th>
            <th class="px-4 py-3 font-medium text-right">
              {{ $t('CALL_REPORTS.TABLE.ANSWER_SPEED') }}
            </th>
            <th class="px-4 py-3 font-medium text-right">
              {{ $t('CALL_REPORTS.TABLE.OUTGOING') }}
            </th>
            <th class="px-4 py-3 font-medium text-right">
              {{ $t('CALL_REPORTS.TABLE.OUTGOING_ANSWERED') }}
            </th>
            <th class="px-4 py-3 font-medium text-right">
              {{ $t('CALL_REPORTS.TABLE.TALK_TIME') }}
            </th>
            <th class="px-6 py-3 font-medium text-right">
              {{ $t('CALL_REPORTS.TABLE.AVG_TALK') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in agents"
            :key="row.agent_id || 'unassigned'"
            class="border-t border-n-weak"
          >
            <td class="px-6 py-3 text-n-slate-12">
              {{ row.name || $t('CALL_REPORTS.TABLE.UNASSIGNED') }}
            </td>
            <td class="px-4 py-3 text-right tabular-nums text-n-slate-12">
              {{ row.incoming_answered }}
            </td>
            <td class="px-4 py-3 text-right tabular-nums text-n-slate-12">
              {{
                row.incoming_answered
                  ? formatDuration(row.avg_answer_seconds)
                  : '—'
              }}
            </td>
            <td class="px-4 py-3 text-right tabular-nums text-n-slate-12">
              {{ row.outgoing }}
            </td>
            <td class="px-4 py-3 text-right tabular-nums text-n-slate-12">
              {{ row.outgoing_answered }}
            </td>
            <td class="px-4 py-3 text-right tabular-nums text-n-slate-12">
              {{ formatDuration(row.talk_seconds) }}
            </td>
            <td class="px-6 py-3 text-right tabular-nums text-n-slate-12">
              {{ formatDuration(row.avg_talk_seconds) }}
            </td>
          </tr>
          <tr v-if="!agents.length && !isLoading">
            <td colspan="7" class="px-6 py-8 text-center text-n-slate-11">
              {{ $t('CALL_REPORTS.TABLE.EMPTY') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
